import * as React from "react"
import { Pencil, X, Zap } from "lucide-react"

import { cn } from "@/lib/utils"

// DEV HANDOFF ─ "Welcome to dlvr.it" first-run modal — the entry point into the
// Create Automation flow. Figma: S311lGXE6JSyTnNAXHmtR7, frame 28750-1480.
//
// Renders the DIALOG SURFACE only (not a portaling <Dialog>) so it stays
// self-contained and pixel-reviewable — same approach as
// new-social-suggest-feeds.tsx. In production, drop this inside your Dialog;
// remember the `.tw-root` portal patch on DialogContent.
//
// The choice cards call `clickHandler` with the chosen entry; the close (X) and
// the "I'll explore on my own" link both call `cancelHandler` with the source
// that triggered them, which the caller uses to tag its analytics event.

export type WelcomeChoice = {
  id: string
  title: string
  description: string
  icon: "automate" | "compose"
  link: string
}

type WelcomeModalProps = {
  className?: string
  title?: string
  subtitle?: string
  choices?: WelcomeChoice[]
  dismissLabel?: string
  cancelHandler: (source: string) => void
  clickHandler: (choice: WelcomeChoice) => void
}

const CHOICES: WelcomeChoice[] = [
  {
    id: "automate",
    title: "Automate posts",
    description: "Auto-post new content from RSS, Instagram, Facebook & more.",
    icon: "automate",
    link: "/share/automate/new/inputs"
  },
  {
    id: "compose",
    title: "Compose a post",
    description: "Create a one-off post and schedule it across your accounts",
    icon: "compose",
    link: "/content/post"
  },
]

function ChoiceCard({ choice, clickHandler }: { choice: WelcomeChoice, clickHandler: (choice: WelcomeChoice) => void }) {
  const Icon = choice.icon === "automate" ? Zap : Pencil
  return (
    <button
      type="button"
      className="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:gap-4 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:px-5 tw:py-6 tw:text-center tw:transition-colors tw:outline-none tw:hover:bg-muted tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
      onClick={() => {
        clickHandler(choice);
      }}
    >
      <span className="tw:text-base tw:font-semibold tw:text-foreground">
        {choice.title}
      </span>
      <Icon className="tw:size-6 tw:text-primary" strokeWidth={1.5} />
      <span className="tw:text-sm tw:text-muted-foreground">
        {choice.description}
      </span>
    </button>
  )
}

export default function WelcomeModal({
  className,
  title = "Welcome to dlvr.it 👋",
  subtitle = "How would you like to start?",
  choices = CHOICES,
  dismissLabel = "I'll explore on my own",
  cancelHandler,
  clickHandler
}: WelcomeModalProps) {
  return (
    <div
      className={cn(
        "tw:relative tw:mx-auto tw:flex tw:w-full tw:max-w-[624px] tw:flex-col tw:items-center tw:gap-6 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:px-10 tw:py-8 tw:text-card-foreground tw:shadow-lg",
        className
      )}
    >
      <button
        type="button"
        aria-label="Close"
        className="tw:absolute tw:top-4 tw:right-4 tw:rounded-xs tw:text-muted-foreground tw:opacity-70 tw:transition-opacity tw:outline-none tw:hover:opacity-100 tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
        onClick={() => {
          cancelHandler("Close button");
        }}
      >
        <X className="tw:size-4" />
      </button>

      <div className="tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:text-center">
        <h2 className="tw:font-heading tw:text-2xl tw:font-semibold tw:text-foreground">
          {title}
        </h2>
        <p className="tw:text-sm tw:text-muted-foreground">{subtitle}</p>
      </div>

      <div className="tw:flex tw:w-full tw:flex-col tw:gap-4 tw:sm:flex-row">
        {choices.map((choice) => (
          <ChoiceCard key={choice.id} choice={choice} clickHandler={clickHandler}/>
        ))}
      </div>

      <button
        type="button"
        className="tw:rounded-xs tw:text-sm tw:font-medium tw:text-foreground tw:underline tw:underline-offset-4 tw:outline-none tw:hover:text-muted-foreground tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
        onClick={() => {
          cancelHandler("Jump link")
        }}
      >
        {dismissLabel}
      </button>
    </div>
  )
}
